import React from 'react';
import { CheckCircle2, Loader2, Printer, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { rupees } from '@/components/comboV2/ChannelPickDialog';

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—');

// Channel + MRP the batch prints with; several channels → staff pick at print; none → can't print
const PriceCell = ({ p, chosen, testId }) => {
  const opts = p?.options || [];
  const ch = p?.channel || (opts.some((o) => o.pricing_type === chosen) ? chosen : null);
  if (ch) {
    const mrp = p?.channel ? p.mrp : opts.find((o) => o.pricing_type === ch)?.mrp;
    return (
      <div data-testid={testId}>
        <span className="font-semibold text-stone-900">{ch}</span> · {rupees(mrp)}
        {ch !== p?.plan_channel && <div className="text-[11px] text-amber-700">{p?.channel ? `no ${p.plan_channel} price` : 'picked'}</div>}
      </div>
    );
  }
  if (opts.length) return <div className="text-xs text-amber-700" data-testid={testId}>Pick at print: {opts.map((o) => `${o.pricing_type} ${rupees(o.mrp)}`).join(' / ')}</div>;
  return <div className="text-xs font-medium text-red-600" data-testid={testId}>No MRP in Rates Master</div>;
};

export const PrintDeskBatches = ({ data, picked, setPicked, busy, onPrint, newKeys, updatedAt, refreshSecs, chosen = {} }) => {
  const batches = data.batches || [];
  const all = batches.length > 0 && picked.size === batches.length;
  const toggle = (k) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const pickedCount = batches.filter((b) => picked.has(b.batch_key)).reduce((n, b) => n + b.count, 0);

  return (
    <Card data-testid="print-desk-batches">
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-stone-900" data-testid="print-desk-staff-name">{data.staff_name} <span className="font-mono text-xs text-stone-500">{data.staff_id}</span></div>
          <div className="text-sm text-stone-500" data-testid="print-desk-total">{batches.length} saved batch(es) · {data.total} sticker(s) waiting</div>
          {updatedAt && (
            <div className="text-xs text-stone-400 flex items-center gap-1 mt-0.5" data-testid="print-desk-updated">
              <RefreshCw className={`h-3 w-3 ${busy ? '' : 'animate-[spin_3s_linear_infinite]'}`} />
              {busy ? 'Auto-refresh paused while printing' : `Updated ${updatedAt.toLocaleTimeString('en-IN')} · refreshes every ${refreshSecs} s`}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy || !picked.size} onClick={() => onPrint([...picked])} data-testid="print-selected-btn">
            <Printer className="h-4 w-4 mr-1" /> Print selected{pickedCount ? ` (${pickedCount})` : ''}
          </Button>
          <Button className="bg-green-600 hover:bg-green-700" disabled={busy || !batches.length} onClick={() => onPrint(batches.map((b) => b.batch_key))} data-testid="print-all-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />} Print all ({data.total})
          </Button>
        </div>
      </div>
      {batches.length === 0 ? (
        <div className="p-10 text-center text-stone-500" data-testid="print-desk-empty">Nothing waiting — all of this staff's stickers are printed.</div>
      ) : (
        <table className="w-full text-sm" data-testid="print-desk-batch-table">
          <thead className="bg-stone-50 text-stone-600">
            <tr>
              <th className="p-3 w-10"><Checkbox checked={all} onCheckedChange={(v) => setPicked(v ? new Set(batches.map((b) => b.batch_key)) : new Set())} data-testid="pick-all-batches" /></th>
              <th className="p-3 text-left">Saved</th>
              <th className="p-3 text-left">Combo</th>
              <th className="p-3 text-left">Design / Shade / Size</th>
              <th className="p-3 text-left">Channel / MRP</th>
              <th className="p-3 text-right">Stickers</th>
              <th className="p-3 text-left">USN codes</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b, i) => (
              <tr key={b.batch_key} className="border-t hover:bg-stone-50 cursor-pointer" onClick={() => toggle(b.batch_key)} data-testid={`batch-row-${i}`}>
                <td className="p-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={picked.has(b.batch_key)} onCheckedChange={() => toggle(b.batch_key)} data-testid={`pick-batch-${i}`} /></td>
                <td className="p-3 whitespace-nowrap">
                  {when(b.saved_at)}
                  {newKeys?.has(b.batch_key) && <span className="ml-2 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 animate-in fade-in" data-testid={`batch-new-${i}`}>NEW</span>}
                </td>
                <td className="p-3"><div className="font-mono">{b.combo_barcode}</div><div className="text-xs text-stone-500">{b.combo_name}</div></td>
                <td className="p-3">{b.design_name} / {b.shade} / {b.size}</td>
                <td className="p-3"><PriceCell p={b.pricing} chosen={chosen[b.session_id]} testId={`batch-price-${i}`} /></td>
                <td className="p-3 text-right font-bold" data-testid={`batch-count-${i}`}>{b.count}</td>
                <td className="p-3" data-testid={`batch-codes-${i}`}>
                  {b.codes_ready >= b.count
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Ready</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing {b.codes_ready}/{b.count}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};
